// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import conversionUtcDate from 'src/utils/conversionUtcDate'

// ** Axios Imports
import axios from 'axios'
import { getWhatsappChatSummary, getWhatsappChatDetail } from '/src/@api/dashboard'
import { generateMockData } from 'src/utils'
import { addDeviceSwitchReset } from 'src/store/apps/deviceSwitchHandlers'
import { selectDeviceRequestKey } from 'src/store/apps/Common'

// ** Fetch User Profile

const createInitialState = () => ({
  leftTotalCount: 0,
  rightTotalCount: 0,
  selectedMessage: {
    contact: {},
    selected: []
  },
  list: [],
  preViewImage: {
    open: false,
    url: ''
  },
  advanced: true,
  fetchLoading: true,
  leftPage: 0,
  rightPage: 1,
  summaryPending: false,
  deviceRequestKey: '',
  activeSummaryRequestId: '',
  activeDetailRequestId: ''
})

const fetchUserProfileRequest = createAsyncThunk('whatsapp/fetchUserProfile', async ({ page, requestKey }) => {
  const deviceUser = JSON.parse(localStorage.getItem('DeviceUser') || '{}')
  const currentDevice = JSON.parse(localStorage.getItem('currentDevice') || '{}')
  const username = deviceUser?.username || ''
  const deviceUdid = currentDevice?.['Unique Identifier'] || ''

  const response = await getWhatsappChatSummary({
    username,
    params: {
      category: 'WHATSAPP_CHAT_SUMMARY',
      offset: (page - 1) * 50,
      limit: 50,
      deviceUdid
    }
  })

  let result =
    response.data.contents?.map(item => {
      return {
        ...item,
        time: conversionUtcDate(item.snippetTimestamp, 'local', 'YYYY-MM-DD'),
        defaultAvata: '/images/avatars/10.png'
      }
    }) || []

  const advanced = username == null || !('advanced' in response) || ['true', true].includes(response?.advanced) // 高级服务开通状态
  if (!advanced) {
    // 未开通高级服务
    result = generateMockData(result).map((item, index) => {
      if (index === 0) return item

      return { ...item, PK: index + 'spyx' }
    })
  }

  let defaultData = []

  if (page === 1 && result[0]) {
    let defaultSelectData = await getWhatsappChatDetail({
      username,
      params: {
        category: 'WHATSAPP_CHAT_DETAIL',
        offset: 0,
        limit: 50,
        deviceUdid,
        rid: String(result[0]?.PK)
      }
    })

    if (defaultSelectData.data.contents != null) {
      defaultData = defaultSelectData.data.contents.map(item => {
        return {
          ...item,
          time: conversionUtcDate(item.date, 'local', 'YYYY-MM-DD HH:mm'),
          defaultAvata: '/images/avatars/10.png'
        }
      })
    }
  }

  return {
    data: result,
    defaultSelectData: defaultData,
    leftPage: page,
    leftTotalCount: response.data.totalCount,
    advanced,
    requestKey
  }
})

export const fetchUserProfile = () => (dispatch, getState) => {
  const state = getState()
  const slice = state.WhatsApp
  if (slice.summaryPending) return Promise.resolve()

  // 已拉完全部会话，避免触底反复请求
  if (slice.leftPage > 0 && slice.leftTotalCount > 0 && slice.list.length >= slice.leftTotalCount) {
    return Promise.resolve()
  }

  const requestKey = selectDeviceRequestKey(state)

  return dispatch(
    fetchUserProfileRequest({
      page: slice.leftPage + 1,
      requestKey
    })
  ).then(action => {
    const latest = getState()
    if (
      fetchUserProfileRequest.fulfilled.match(action) &&
      action.payload.leftPage === 1 &&
      selectDeviceRequestKey(latest) === requestKey &&
      latest.WhatsApp.activeSummaryRequestId === action.meta.requestId
    ) {
      localStorage.setItem('whatsappItem', JSON.stringify(action.payload.data[0] ?? ''))
    }

    return action
  })
}

// ** Select Message

const selectMessageRequest = createAsyncThunk(
  'whatsapp/selectChat',
  async ({ contact, page, requestKey }) => {
    const deviceUser = JSON.parse(localStorage.getItem('DeviceUser') || '{}')
    const currentDevice = JSON.parse(localStorage.getItem('currentDevice') || '{}')

    const response = await getWhatsappChatDetail({
      username: deviceUser?.username || '',
      params: {
        category: 'WHATSAPP_CHAT_DETAIL',
        offset: (page - 1) * 50,
        limit: 50,
        deviceUdid: currentDevice?.['Unique Identifier'] || '',
        rid: String(contact?.PK)
      }
    })

    let result = []

    if (response.data.contents != null) {
      result = response.data.contents.map(item => ({
        ...item,
        time: conversionUtcDate(item.date, 'local', 'YYYY-MM-DD HH:mm'),
        defaultAvata: '/images/avatars/10.png'
      }))
    }

    return { contact, selected: result, rightTotalCount: response.data.totalCount, page, requestKey }
  }
)

export const selectMessage = obj => (dispatch, getState) => {
  const state = getState()
  const slice = state.WhatsApp
  let contact = obj || slice.selectedMessage.contact
  if (!contact?.PK) contact = JSON.parse(localStorage.getItem('whatsappItem') || '{}')
  if (!contact?.PK) return Promise.resolve()

  return dispatch(
    selectMessageRequest({
      contact,
      page: obj ? 1 : slice.rightPage + 1,
      requestKey: selectDeviceRequestKey(state)
    })
  )
}

export const setPerViewImage = createAsyncThunk('whatsapp/setPerView', async (obj, { dispatch }) => {
  return obj
})

export const appChatSlice = createSlice({
  name: 'WhatsApp',
  initialState: createInitialState(),
  reducers: {
    removeSelectedChat: state => {
      state.selectedChat = null
    }
  },
  extraReducers: builder => {
    addDeviceSwitchReset(builder, createInitialState)

    builder.addCase(fetchUserProfileRequest.pending, (state, action) => {
      // 仅首屏全页 loading；分页用 summaryPending 在列表底部转圈，避免卸载列表导致回顶
      if (action.meta.arg.page === 1) {
        state.fetchLoading = true
      }
      state.summaryPending = true
      state.deviceRequestKey = action.meta.arg.requestKey
      state.activeSummaryRequestId = action.meta.requestId
    })

    builder.addCase(fetchUserProfileRequest.fulfilled, (state, action) => {
      if (
        state.deviceRequestKey !== action.meta.arg.requestKey ||
        state.activeSummaryRequestId !== action.meta.requestId
      ) return

      const advanced = action.payload.advanced === true || action.payload.advanced === 'true'
      if (advanced && action.payload.leftPage === 1) {
        state.list = [...action.payload.data]
      } else if (advanced) {
        state.list = [...state.list, ...action.payload.data]
      } else {
        state.list = [...action.payload.data]
      }

      state.leftPage = advanced ? action.payload.leftPage : 0
      // 分页空结果视为已到底，避免触底空转请求
      if (
        advanced &&
        action.payload.leftPage > 1 &&
        (!action.payload.data || action.payload.data.length === 0)
      ) {
        state.leftTotalCount = state.list.length
      } else {
        state.leftTotalCount = action.payload.leftTotalCount
      }
      if (action.payload.leftPage == 1) {
        state.selectedMessage = { contact: action.payload.data[0], selected: action.payload.defaultSelectData }
      }
      state.advanced = advanced
      state.fetchLoading = false
      state.summaryPending = false
    })

    builder.addCase(fetchUserProfileRequest.rejected, (state, action) => {
      if (
        state.deviceRequestKey === action.meta.arg.requestKey &&
        state.activeSummaryRequestId === action.meta.requestId
      ) {
        state.fetchLoading = false
        state.summaryPending = false
      }
    })

    builder.addCase(selectMessageRequest.pending, (state, action) => {
      state.deviceRequestKey = action.meta.arg.requestKey
      state.activeDetailRequestId = action.meta.requestId
    })

    builder.addCase(selectMessageRequest.fulfilled, (state, action) => {
      if (
        state.deviceRequestKey !== action.meta.arg.requestKey ||
        state.activeDetailRequestId !== action.meta.requestId
      ) return

      if (action.payload.page > 1 && state.selectedMessage.contact.PK == action.payload.contact.PK) {
        state.selectedMessage.selected = [...state.selectedMessage.selected, ...action.payload.selected]
      } else {
        state.selectedMessage.selected = action.payload.selected
      }

      state.selectedMessage.contact = action.payload.contact
      state.rightTotalCount = action.payload.rightTotalCount
      state.rightPage = action.payload.page
    })
    builder.addCase(setPerViewImage.fulfilled, (state, action) => {
      state.preViewImage = action.payload
    })
  }
})

export const { removeSelectedChat } = appChatSlice.actions

export default appChatSlice.reducer
